// src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import authAPI from "../api/auth";
import ReCaptcha from "../components/ReCaptcha";
import AuthLegalFooter from "../components/AuthLegalFooter";
import { isAuthenticated } from "../utils/auth";
import { setAuthTokenCookie, touchLastActivity } from "../utils/sessionIdle";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [error, setError] = useState("");
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [loadingRegistrationStatus, setLoadingRegistrationStatus] = useState(true);
  const [step, setStep] = useState("credentials");
  const [preAuthToken, setPreAuthToken] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  // Sub-user 2FA is compulsory, so a sub-user login can land on a forced-setup step.
  const [isSubUserLogin, setIsSubUserLogin] = useState(false);
  const [setupToken, setSetupToken] = useState("");
  const [setupQr, setSetupQr] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated()) {
      const token = localStorage.getItem("authToken");
      if (token) {
        setAuthTokenCookie(token);
      }

      const searchParams = new URLSearchParams(location.search);
      const returnUrl = searchParams.get("return_url");

      if (returnUrl) {
        window.location.href = returnUrl;
        return;
      }

      navigate("/dashboard", { replace: true });
    }
  }, [navigate, location]);

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8010"}/public/registration-status`);
        if (response.ok) {
          const data = await response.json();
          setRegistrationEnabled(data.enabled);
        }
      } catch {
        setRegistrationEnabled(true);
      } finally {
        setLoadingRegistrationStatus(false);
      }
    };

    checkRegistrationStatus();
  }, []);

  const finishLogin = (token, user) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("user", JSON.stringify(user || {}));
    touchLastActivity(true);
    setAuthTokenCookie(token);

    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("return_url");

    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    const from = location.state?.from?.pathname || "/dashboard";
    navigate(from, { replace: true });
    setTimeout(() => window.location.assign(from), 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCaptchaError("");

    if (!captcha || !captcha.recaptcha_token) {
      setCaptchaError("Please complete the security verification");
      return;
    }

    try {
      setSubmitting(true);
      const res = await authAPI.login(username, password, captcha, rememberMe);

      // Sub-user with no 2FA yet: compulsory first-time setup before anything else.
      if (res?.requires_2fa_setup && res?.setup_token) {
        setIsSubUserLogin(true);
        setSetupToken(res.setup_token);
        setTwoFACode("");
        setError("");
        setStep("2fa-setup");
        setSetupLoading(true);
        try {
          const data = await authAPI.subUserStart2FASetup(res.setup_token);
          setSetupQr(data);
        } catch (err) {
          setError(err?.response?.data?.detail || "Could not start two-factor setup. Please sign in again.");
        } finally {
          setSetupLoading(false);
        }
        return;
      }

      if (res?.requires_2fa && res?.pre_auth_token) {
        setIsSubUserLogin(Boolean(res.is_sub_user));
        setPreAuthToken(res.pre_auth_token);
        setStep("2fa");
        setTwoFACode("");
        setError("");
        return;
      }

      const token = res?.access_token;
      if (!token) throw new Error("No token received");
      finishLogin(token, res.user);
    } catch (err) {
      const errorDetail = err?.response?.data?.detail || err?.message || "Login failed";

      if (String(errorDetail).includes("CAPTCHA")) {
        setCaptchaError(errorDetail);
        setError("");
      } else if (String(errorDetail).includes("Invalid credentials")) {
        setError("Invalid email or password");
        setCaptchaError("");
      } else if (String(errorDetail).includes("not confirmed")) {
        setError("Please confirm your email before logging in");
        setCaptchaError("");
      } else {
        setError(errorDetail);
        setCaptchaError("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!twoFACode.trim()) {
      setError("Enter the 6-digit code from your authenticator app");
      return;
    }
    try {
      setSubmitting(true);
      const res = isSubUserLogin
        ? await authAPI.subUserVerify2FALogin(preAuthToken, twoFACode.trim(), rememberMe)
        : await authAPI.verify2FALogin(preAuthToken, twoFACode.trim(), trustDevice, rememberMe);
      if (!res?.access_token) throw new Error("No token received");
      finishLogin(res.access_token, res.user);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Invalid verification code");
    } finally {
      setSubmitting(false);
    }
  };

  const handle2FASetupSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!twoFACode.trim()) {
      setError("Enter the 6-digit code from your authenticator app");
      return;
    }
    try {
      setSubmitting(true);
      const res = await authAPI.subUserVerify2FASetup(setupToken, twoFACode.trim());
      if (!res?.access_token) throw new Error("No token received");
      finishLogin(res.access_token, res.user);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Invalid verification code");
    } finally {
      setSubmitting(false);
    }
  };

  const loginLabel = {
    display: "block",
    color: "#475569",
    fontSize: 12,
    marginBottom: 6,
    fontWeight: 500,
  };

  const loginInput = {
    width: "100%",
    borderRadius: 8,
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    color: "#1e293b",
    padding: "12px",
    fontSize: 14,
    boxSizing: "border-box",
  };

  return (
    <div className="auth-page">
      <div className="card auth-card">
        <h2 className="page-title">{step === "2fa-setup" ? "Set up two-factor authentication" : step === "2fa" ? "Two-factor authentication" : "Sign in"}</h2>

        {step === "credentials" ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 12 }}>
              <label style={loginLabel}>Email</label>
              <input
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="you@example.com"
                style={loginInput}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={loginLabel}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ ...loginInput, paddingRight: "44px" }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", padding: 4 }}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            <ReCaptcha
              onCaptchaChange={(captchaData) => {
                setCaptcha(captchaData);
                setCaptchaError("");
              }}
              error={captchaError}
            />

            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#475569", marginBottom: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>Remember me for 30 days (stay signed in while you use the app)</span>
            </label>

            {error && (
              <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 8, padding: 8, backgroundColor: "#fef2f2", borderRadius: 6 }}>
                {error}
              </div>
            )}
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? "Signing in…" : "Continue"}
            </button>
          </form>
        ) : step === "2fa-setup" ? (
          <form onSubmit={handle2FASetupSubmit}>
            <p style={{ fontSize: 14, color: "#64748b", marginTop: 0, lineHeight: 1.5 }}>
              Two-factor authentication is required for sub-user accounts. Scan this code with
              an authenticator app (Google Authenticator, Authy, 1Password…), then enter the
              6-digit code it shows to finish signing in.
            </p>
            {setupLoading && <p style={{ fontSize: 13, color: "#64748b" }}>Preparing your code…</p>}
            {setupQr?.qr_code_base64 && (
              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <img
                  src={`data:image/png;base64,${setupQr.qr_code_base64}`}
                  alt="Two-factor authentication QR code"
                  style={{ width: 180, height: 180, border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, background: "#fff" }}
                />
              </div>
            )}
            {setupQr?.secret && (
              <p style={{ fontSize: 12, color: "#64748b", textAlign: "center", marginTop: 0, marginBottom: 12, wordBreak: "break-all" }}>
                Can't scan? Enter this key manually: <strong>{setupQr.secret}</strong>
              </p>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={loginLabel}>Authentication code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                style={loginInput}
                placeholder="000000"
              />
            </div>
            <p style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 6, padding: 8, marginBottom: 12 }}>
              Keep your authenticator app — you'll need a code every time you sign in. If you
              lose access, ask the account owner to reset your two-factor setup.
            </p>
            {error && (
              <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 8, padding: 8, backgroundColor: "#fef2f2", borderRadius: 6 }}>
                {error}
              </div>
            )}
            <div className="toolbar-responsive toolbar-stack-sm" style={{ gap: 8 }}>
              <button type="submit" className="btn btn--primary" disabled={submitting || setupLoading}>
                {submitting ? "Verifying…" : "Verify & finish setup"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={submitting}
                onClick={() => {
                  setStep("credentials");
                  setSetupToken("");
                  setSetupQr(null);
                  setTwoFACode("");
                  setIsSubUserLogin(false);
                  setError("");
                }}
              >
                Back
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handle2FASubmit}>
            <p style={{ fontSize: 14, color: "#64748b", marginTop: 0, lineHeight: 1.5 }}>
              Enter the 6-digit code from your authenticator app for <strong>{username}</strong>.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={loginLabel}>Authentication code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                style={loginInput}
                placeholder="000000"
              />
            </div>
            {!isSubUserLogin && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#475569", marginBottom: 16, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>Trust this browser for 15 days (skip 2FA on this device)</span>
              </label>
            )}
            {error && (
              <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 8, padding: 8, backgroundColor: "#fef2f2", borderRadius: 6 }}>
                {error}
              </div>
            )}
            <div className="toolbar-responsive toolbar-stack-sm" style={{ gap: 8 }}>
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {submitting ? "Verifying…" : "Verify & sign in"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={submitting}
                onClick={() => {
                  setStep("credentials");
                  setPreAuthToken("");
                  setTwoFACode("");
                  setIsSubUserLogin(false);
                  setError("");
                }}
              >
                Back
              </button>
            </div>
          </form>
        )}

        <div style={{ marginTop: 16, textAlign: "center" }}>
          {step === "credentials" && (
            <>
              <Link
                to="/forgot-password"
                style={{ color: "#3b82f6", fontSize: 14, textDecoration: "none", display: "block", marginBottom: 8 }}
              >
                Forgot password?
              </Link>
              {!loadingRegistrationStatus && registrationEnabled && (
                <div style={{ color: "#64748b", fontSize: 14 }}>
                  Don&apos;t have an account?{" "}
                  <Link to="/register" style={{ color: "#3b82f6", textDecoration: "none" }}>
                    Register here
                  </Link>
                </div>
              )}
              {!loadingRegistrationStatus && !registrationEnabled && (
                <div style={{ color: "#64748b", fontSize: 14, fontStyle: "italic" }}>
                  Registration is currently disabled by administrator
                </div>
              )}
            </>
          )}
          {location.state?.message && (
            <div style={{ color: "#dc2626", fontSize: 14, marginTop: 8, padding: 8, backgroundColor: "#fef2f2", borderRadius: 6, border: "1px solid #fecaca" }}>
              {location.state.message}
            </div>
          )}
        </div>

        <AuthLegalFooter />
      </div>
    </div>
  );
}

export default Login;
